import { createPortal } from "react-dom";
import { Icon } from "@/components/primitives";

// PhotoSourcePicker — themed bottom-sheet replacement for iOS's native
// CameraSource.Prompt action sheet. Triggered from photo-slot "+" buttons
// in Scan.tsx; offers Take Photo vs Choose from Library. After the user
// picks, the parent calls pickNativePhoto("camera") or ("library") so iOS
// opens the appropriate native surface directly — no more dark-grey
// system action sheet that clashes with FIG's palette.
//
// Portals to document.body to escape MobileFrame's `position: relative;
// zIndex: 5` stacking trap (same pattern as BadgeDetailModal +
// ListingStatusSheet). Backdrop has touch-action: none so dragging it
// doesn't scroll the page beneath; sheet body has overscroll-behavior
// contain + touch-action pan-y.

interface Props {
  open: boolean;
  onCamera: () => void;
  onLibrary: () => void;
  onCancel: () => void;
}

export function PhotoSourcePicker({ open, onCamera, onLibrary, onCancel }: Props) {
  if (!open) return null;

  const sheet = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add a photo"
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0,
        background: "hsla(0,0%,0%,0.55)",
        zIndex: 100,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        touchAction: "none",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 430,
          background: "hsl(var(--cream))",
          borderTop: "1.5px solid hsl(var(--ink))",
          padding: "20px 20px calc(20px + env(safe-area-inset-bottom, 0px))",
          maxHeight: "85vh",
          display: "flex", flexDirection: "column", gap: 10,
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 4,
        }}>
          <div className="caps" style={{ color: "hsl(var(--rust))" }}>
            Add a photo
          </div>
          <button
            onClick={onCancel}
            aria-label="Cancel"
            style={{
              background: "none", border: "none", padding: 4, cursor: "pointer",
              color: "hsl(var(--ink-2))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 16,
            }}
          >
            ×
          </button>
        </div>

        <button
          onClick={onCamera}
          style={{
            width: "100%",
            display: "flex", alignItems: "center", gap: 14,
            padding: "16px 18px",
            background: "hsl(var(--paper))",
            border: "1.5px solid hsl(var(--ink))",
            cursor: "pointer", textAlign: "left",
          }}
        >
          <span style={{ flexShrink: 0, color: "hsl(var(--ink))" }}>
            {Icon.camera(28)}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontWeight: 800, fontSize: 17,
              color: "hsl(var(--ink))",
              lineHeight: 1.15,
            }}>
              Take a photo
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              color: "hsl(var(--ink-2))", letterSpacing: "0.06em",
              marginTop: 4,
            }}>
              Open the camera viewfinder
            </div>
          </div>
          <span style={{ flexShrink: 0, color: "hsl(var(--ink-2))" }}>
            {Icon.chev(14, "r")}
          </span>
        </button>

        <button
          onClick={onLibrary}
          style={{
            width: "100%",
            display: "flex", alignItems: "center", gap: 14,
            padding: "16px 18px",
            background: "hsl(var(--paper))",
            border: "1.5px solid hsl(var(--ink))",
            cursor: "pointer", textAlign: "left",
          }}
        >
          <span style={{
            flexShrink: 0, color: "hsl(var(--ink))",
            // Library icon — a simple stack of two rectangles. Inline SVG
            // so it picks up the current text color across themes.
            width: 28, height: 28,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="7" width="14" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="7" y="3" width="14" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
            </svg>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontWeight: 800, fontSize: 17,
              color: "hsl(var(--ink))",
              lineHeight: 1.15,
            }}>
              From your library
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              color: "hsl(var(--ink-2))", letterSpacing: "0.06em",
              marginTop: 4,
            }}>
              Choose an existing photo
            </div>
          </div>
          <span style={{ flexShrink: 0, color: "hsl(var(--ink-2))" }}>
            {Icon.chev(14, "r")}
          </span>
        </button>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
